/**
 * Track → scene graph.
 *
 * Placement conventions (must match src/gen):
 * - `pos` is the world position of the piece's authored origin (bottom-center).
 * - `rotation` r ∈ 0..3 → `rotation.y = r · π/2`.
 * - `scale` is uniform and decor-only; `mirrored` flips x.
 */
import * as THREE from "three";
import type { Placement, Track } from "../shared/types.ts";
import type { Assets } from "./assets.ts";
import { drawPipeColor } from "./pipeColor.ts";

export interface Spinner {
  obj: THREE.Object3D;
  axis: THREE.Vector3;
  radPerSec: number;
}

/**
 * Instantiate placements into `group` (shared by finite tracks and endless
 * segments). Returns materials cloned for goal tinting — the caller owns
 * their disposal; piece clones share the loaded kits' resources.
 */
export function instantiatePlacements(
  placements: readonly Placement[],
  assets: Assets,
  group: THREE.Group,
  spinners: Spinner[],
  missing: Set<string>,
  /** Per-placement pipe color (endless island chapters); null = stock. */
  pipeColorFor?: (p: Placement) => readonly [number, number, number] | null,
): THREE.Material[] {
  const goalMaterials: THREE.Material[] = [];
  for (const [index, p] of placements.entries()) {
    const obj = assets.getPiece(p.pieceId);
    if (!obj) {
      missing.add(p.pieceId);
      continue;
    }
    // Inspect mode resolves picked meshes back to their placement.
    obj.userData.placementIndex = index;
    // Endless follow-camera collision uses the authored semantic role to
    // avoid track walls/floors while ignoring support and scenery clutter.
    obj.userData.placementRole = p.role;
    if (!p.goal && pipeColorFor) {
      assets.applyPipeColor(obj, pipeColorFor(p));
    }
    obj.position.set(p.pos[0], p.pos[1], p.pos[2]);
    obj.rotation.y = p.rotation * Math.PI / 2;
    const s = p.scale ?? 1;
    obj.scale.setScalar(s);
    if (p.mirrored) obj.scale.x = -s;
    if (p.goal) {
      // Lap-line gate: charcoal posts + a checkered cloth slab — a finish
      // flag against any palette.
      obj.traverse((child: THREE.Object3D) => {
        const mesh = child as THREE.Mesh;
        if (!mesh.isMesh) return;
        const mats = Array.isArray(mesh.material)
          ? mesh.material
          : [mesh.material];
        const tinted = mats.map((m: THREE.Material) => {
          const c = (m as THREE.MeshStandardMaterial).clone();
          c.color.set(0x30303a);
          goalMaterials.push(c);
          return c;
        });
        mesh.material = Array.isArray(mesh.material) ? tinted : tinted[0];
      });
      const checker = makeGoalChecker();
      if (checker) {
        obj.add(checker.mesh);
        goalMaterials.push(checker.material);
      }
    }
    group.add(obj);

    if (p.spin) {
      // Spin only the blade sub-node when the piece has one (fan pieces have
      // a child authored "fan" at the hub — GLTFLoader uniquifies duplicate
      // node names across the file, so it loads as "fan", "fan_1", "fan_2"…).
      // Spinning the whole placement would tumble the housing.
      let target: THREE.Object3D = obj;
      obj.traverse((child: THREE.Object3D) => {
        if (child !== obj && /^fan(_\d+)?$/.test(child.name)) target = child;
      });
      // The planner says [0,0,1] for every fan, but only standing fans have
      // their blade disc in the x/y plane. Table fans (fan-two/-four,
      // fan-simple-*) lie flat with the disc in x/z and must spin about
      // local +y. The blade's own bbox tells them apart: its thin dimension
      // IS the hub axis.
      const axis = new THREE.Vector3(
        p.spin.axis[0],
        p.spin.axis[1],
        p.spin.axis[2],
      ).normalize();
      const blade = target as THREE.Mesh;
      if (target !== obj && blade.isMesh && blade.geometry) {
        blade.geometry.computeBoundingBox();
        const size = blade.geometry.boundingBox!.getSize(new THREE.Vector3());
        if (size.y <= size.x && size.y <= size.z) axis.set(0, 1, 0);
        else if (size.z <= size.x && size.z <= size.y) axis.set(0, 0, 1);
        else axis.set(1, 0, 0);
      }
      spinners.push({
        obj: target,
        axis,
        radPerSec: p.spin.radPerSec,
      });
    }
  }
  return goalMaterials;
}

export interface BuiltTrack {
  group: THREE.Group;
  spinners: Spinner[];
  /** World bounds of the placed pieces (excludes the ground plane). */
  box: THREE.Box3;
  missing: string[];
  dispose(): void;
}

/** Ground overshoot, in grid units — wide enough for the fog to eat its edge. */
const GROUND_MARGIN = 90;

// ---------------------------------------------------------------------------
// Goal gate checker — the lap line reads as a finish flag, not a tinted
// banner. The banner mesh is one primitive on the shared colormap (no cloth
// submesh to retexture), so a thin checkered slab wraps the cloth band:
// banner-high posts sit at ±0.425 with the cloth spanning ~y 0.75–0.95.
// ---------------------------------------------------------------------------

let checkerTexture: THREE.CanvasTexture | null = null;
const checkerGeometry = new THREE.BoxGeometry(0.86, 0.2, 0.095);

function getCheckerTexture(): THREE.CanvasTexture | null {
  if (checkerTexture) return checkerTexture;
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 64;
  const g = canvas.getContext("2d");
  if (!g) return null;
  const COLS = 8;
  const ROWS = 2;
  for (let cx = 0; cx < COLS; cx++) {
    for (let cy = 0; cy < ROWS; cy++) {
      g.fillStyle = (cx + cy) % 2 === 0 ? "#f2f2ee" : "#1c1c22";
      g.fillRect(
        (cx * canvas.width) / COLS,
        (cy * canvas.height) / ROWS,
        canvas.width / COLS,
        canvas.height / ROWS,
      );
    }
  }
  checkerTexture = new THREE.CanvasTexture(canvas);
  checkerTexture.colorSpace = THREE.SRGBColorSpace;
  return checkerTexture;
}

/** Checkered cloth slab, positioned in the banner's local frame. The caller
 * owns the material's disposal (geometry + texture are shared). */
function makeGoalChecker():
  | { mesh: THREE.Mesh; material: THREE.Material }
  | null {
  const tex = getCheckerTexture();
  if (!tex) return null;
  const material = new THREE.MeshStandardMaterial({
    map: tex,
    roughness: 0.9,
    metalness: 0,
  });
  const mesh = new THREE.Mesh(checkerGeometry, material);
  mesh.position.set(0, 0.85, 0);
  mesh.castShadow = true;
  return { mesh, material };
}

export function buildTrack(
  track: Track,
  assets: Assets,
  scene: THREE.Scene,
): BuiltTrack {
  assets.applyPalette(track.stats.paletteIndex, drawPipeColor(track.seed));

  const group = new THREE.Group();
  group.name = `track:${track.seed}`;
  const spinners: Spinner[] = [];
  const missing = new Set<string>();
  // Goal-gate placements get cloned, tinted materials (the kit shares one
  // colormap material — mutating it would restyle every piece).
  const goalMaterials = instantiatePlacements(
    track.placements,
    assets,
    group,
    spinners,
    missing,
  );

  for (const id of missing) {
    console.warn(`[track] unknown pieceId "${id}" — skipped`);
  }

  const box = new THREE.Box3().setFromObject(group);
  if (box.isEmpty()) {
    box.set(new THREE.Vector3(-8, 0, -8), new THREE.Vector3(8, 4, 8));
  }

  // Ground: sized to the track plus a generous margin so the horizon reads as
  // ground rather than sky at any camera angle.
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  const extent = Math.max(size.x, size.z) + GROUND_MARGIN * 2;
  const ground = new THREE.Mesh(
    new THREE.PlaneGeometry(extent, extent),
    new THREE.MeshStandardMaterial({
      color: 0x6f9c58,
      roughness: 1,
      metalness: 0,
    }),
  );
  ground.name = "ground";
  ground.rotation.x = -Math.PI / 2;
  ground.position.set(center.x, 0, center.z);
  ground.receiveShadow = true;
  group.add(ground);

  scene.add(group);

  return {
    group,
    spinners,
    box,
    missing: [...missing],
    dispose() {
      scene.remove(group);
      // Only the ground's geometry/material and the goal gate's cloned
      // materials are owned by this build; piece clones share the loaded
      // kit's resources, so disposing them would break every future build.
      ground.geometry.dispose();
      (ground.material as THREE.Material).dispose();
      for (const m of goalMaterials) m.dispose();
      group.clear();
    },
  };
}
